
import EndorsementMarquee from '../../components/frontfacing/EndorsementMarquee';
import Footer from '../../components/frontfacing/Footer';
import GetSignals from '../../components/teams/GetSignals';
import { staticImageUrl } from '../../util/images';
import { Headings, Paragraph } from '../../components/UI/TextStyles';
import { hideOnMobile } from '../../style/misc';
import { scale } from '../../util/scale';
import { colors, spacing } from '../../style/theme';
import { ArrowNavButton } from '../../components/buttons/ArrowNavButton';

const nametag = scale({
  borderRadius: 10,
  overflow: 'hidden',
  display: 'inline-block',
  img: {
    height: [50, 50, 60],
  },
});

const green = scale({
  color: colors.wrenGreen,
});

const Sources = () => (
  <div css={nametag}>
    <img src={staticImageUrl('kirsten-harris-eavolu.png')} />
  </div>
);

const ProductsUsed = () => (
  <div css={nametag}>
    <img src={staticImageUrl('integrations-nametag.png')} />
  </div>
);

const InsightCard = () => (
  <div
    css={scale({
      display: ['block', 'block', 'flex'],
      marginTop: spacing.large,
      marginBottom: spacing.large,
      '& > *': {
        marginRight: ['none', 'none', spacing.large],
        marginBottom: [spacing.large, spacing.large, 'none'],
      },
    })}
  >
    <div
      css={scale({
        paddingRight: spacing.large,
        borderRight: ['none', 'none', `1px solid #ccc`],
      })}
    >
      <p>Insights from:</p>
      <Sources />
    </div>
    <div>
      <p>Products used:</p>
      <ProductsUsed />
    </div>
  </div>
);

export default function CaseStudy() {
  return (
    <>
      <div
        css={scale({
          width: '90%',
          maxWidth: 740,
          overflowX: ['hidden', 'hidden', 'visible'],
          margin: 'auto',
          section: {
            padding: `0 ${spacing.default}px`,
            marginBottom: spacing.xLarge,
          },
          h1: { marginBottom: spacing.small },
          p: {
            color: colors.textSecondary,
            marginBottom: spacing.medium,
            lineHeight: 1.4,
            fontSize: '110%',
          },
          h3: {
            marginBottom: spacing.small,
            fontWeight: 600,
            fontSize: '110%',
          },
        })}
      >
        <div
          css={[
            {
              display: 'flex',
              justifyContent: 'center',
              marginBottom: -spacing.large,
              position: 'relative',
            },
            hideOnMobile,
          ]}
        />
        <div css={scale({ marginTop: ['none', 'none', spacing.massive] })} />

        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            a: { textDecoration: 'none !important' },
            marginBottom: spacing.xLarge,
          }}
        >
          <a href="/business">← Back to all solutions</a>
        </div>

        <img
          src={staticImageUrl('eavolu-logo.png')}
          css={{ width: 120, marginBottom: spacing.medium }}
        />

        <Headings.XLarge
          css={scale({
            paddingRight: ['none', 'none', spacing.massive],
          })}
        >
          How eavolu builds a{' '}
          <span css={green}>holistic sustainability model</span> and offsets
          what they can’t reduce with Wren.
        </Headings.XLarge>

        <Paragraph>
          <a
            css={{ color: colors.linkBlue, textDecoration: 'none' }}
            target="_blank"
            href="https://www.eavolu.com"
          >
            eavolu
          </a>{' '}
          creates sustainable, eco-luxe garments while rethinking every aspect
          of their operations— from fabric sourcing to packaging to energy
          use—so fashion leaves a lighter footprint.
        </Paragraph>

        <InsightCard />

        <Headings.XLarge>1.</Headings.XLarge>
        <Headings.Small>
          Integrating sustainability into every step of production.
        </Headings.Small>
        <Paragraph>
          eavolu garments are made locally in Los Angeles by their dedicated
          Garment Team and Factory Lead, minimizing transportation emissions and
          ensuring ethical production.
        </Paragraph>
        <Paragraph>
          Packaging is fully compostable—even worm-bin friendly—and tested
          on-site. Offices run on solar power, rain barrels irrigate outdoor
          plants, and even the bins products ship from are recyclable or
          compostable.
        </Paragraph>
        <Paragraph>
          Through innovation, eavolu also rescues “deadstock” materials left
          behind by other brands. Their Untamed Collection, for example,
          repurposes 100% organic cotton in unwanted colors, washed and
          redesigned into new garments to prevent landfill waste.
        </Paragraph>
        <img
          css={{
            maxWidth: '120%',
            marginTop: spacing.medium,
            marginBottom: spacing.large,
            marginLeft: '-10%',
          }}
          src={staticImageUrl('eavolu-hero.png', { w: 2000 })}
        />

        <Headings.XLarge>2.</Headings.XLarge>
        <Headings.Small>
          Reducing impact first, then offsetting the rest.
        </Headings.Small>
        <Paragraph>
          eavolu takes pride in reducing their footprint at every
          turn—composting food scraps in a worm bin, choosing organic cotton
          labels, and prioritizing reclaimed fabrics. But some emissions are
          unavoidable.
        </Paragraph>
        <Paragraph>
          That’s where Wren comes in. eavolu partners with Wren to offset what
          can’t be eliminated, investing in high-quality climate projects that
          align with their values.
        </Paragraph>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img
            css={{
              marginTop: spacing.medium,
              marginBottom: spacing.large,
              width: '100%',
            }}
            src={staticImageUrl(
              'eavolu-marty-ananya-la-warehouse-cropped.jpg',
              { w: 2000 }
            )}
          />
        </div>
        <Paragraph>
          This blended approach—reduce first, then offset—ensures that
          sustainability is not a marketing add-on, but a core operating
          principle. The result is a company model designed to inspire both
          customers and peers in the fashion industry.
        </Paragraph>

        <Headings.XLarge>3.</Headings.XLarge>
        <Headings.Small>
          Customer connection and long-term vision.
        </Headings.Small>
        <Paragraph>
          eavolu’s eco-luxe garments resonate with customers who want quality
          and conscious choices. The brand’s initiatives build trust and deepen
          loyalty—proof that sustainability can enhance customer relationships.
        </Paragraph>
        <Paragraph>
          Looking ahead, eavolu plans to expand their reclaimed-fabric
          collections, push further into compostable/recyclable innovations, and
          keep offsetting unavoidable emissions with Wren’s verified climate
          projects.
        </Paragraph>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img
            css={{
              marginTop: spacing.medium,
              marginBottom: spacing.large,
              width: '100%',
            }}
            src={staticImageUrl('eavolu-factory-visit.jpg', { w: 2000 })}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: 500,
            margin: 'auto',
            textAlign: 'center',
            marginTop: spacing.massive,
          }}
        >
          <Headings.Large>Inspired by eavolu’s story?</Headings.Large>
          <Paragraph>
            Schedule a call with Wren’s sustainability team to learn how your
            business can start reducing and offsetting emissions.
          </Paragraph>
          <ArrowNavButton
            color={colors.textPrimary}
            href={`/teams/contact`}
            buttonType="filled"
            shape="pill"
            css={{ fontWeight: '600' }}
          >
            Contact sales
          </ArrowNavButton>
        </div>
      </div>

      <section css={{ marginTop: spacing.massive }}>
        <EndorsementMarquee />
      </section>
      <Footer hideCTA />
      <GetSignals />
    </>
  );
}
